import React from 'react';
import { Link } from 'react-router-dom';
import { Info, CheckCircle2 } from 'lucide-react';
import NotificationPageLayout, { CodeBlock } from '../components/NotificationPageLayout';
import LangTabs from '../components/LangTabs';

const TOC = [
  { id: 'overview', label: 'Overview' },
  { id: 'setting-up-handler', label: 'Setting Up A Handler', children: [
    { id: 'sample-notification-url', label: 'Sample Notification URL' },
    { id: 'sample-handler', label: 'Sample Notification Handler' },
  ]},
  { id: 'data-returned', label: 'Data Returned' },
  { id: 'sample-xml', label: 'Sample XML Document' },
  { id: 'checklist', label: 'Implementation Checklist' },
];

const NOTIFICATION_URL_SAMPLE = `http://example.com/Notification-Handler/Confirmation/?data=<R>...</R>`;

const PHP_SAMPLE = `<?php
// Post Request - Confirmation Notification
if(isset($_POST["data"]))
{
    $strData = $_POST["data"]; //Assign the data to variable $strData
    $XMLDataArray = simplexml_load_string($strData); //Used to parse XML at ease
    //...

    /* Obtain information from response fields */
    $ResponseR1 = (string)$XMLDataArray->R1;
    $ResponseR2 = (string)$XMLDataArray->R2;
    //... etc

    //Or loop through the elements
    /* END */

    //More code...
}
?>`;

const CSHARP_SAMPLE = `// Post Request - Confirmation Notification
[HttpPost]
public IActionResult NotificationURL(string data)
{
    /* Parse XML from response */
    XmlDocument xDoc = new XmlDocument();
    xDoc.LoadXml(data);
    /* END */

    /* Obtain information from response fields */
    XmlNodeList ResponseR1 = xDoc.GetElementsByTagName("R1");
    XmlNodeList ResponseR2 = xDoc.GetElementsByTagName("R2");
    //... etc

    //Or loop through the elements
    /* END */

    //More code...

    return View();
}`;

const SAMPLE_XML = `<!-- Sample XML Document -->
<?xml version="1.0"?>
<R>
  <R1>0000</R1>
  <R2>Successful</R2>
  <R3>43DAAL4B-78CF-4000-AC6B-EBA1F1116229</R3>
  <R4>Successful</R4>
  <R5>Data Not Checked</R5>
  <R8>Match</R8>
  <R9>Ok</R9>
  <R10>Confirm</R10>
  <R11>MerchantData</R11>
  <R12>MerchantReferenceNumber</R12>
</R>`;

const FIELDS = [
  {
    name: 'R1',
    desc: (
      <>
        Error Code.
        <br /><br />
        Please see <Link to="/technical-documentation/appendix/error-codes" className="text-[#1F4E79] underline hover:text-[#163a5f] font-semibold">Error Codes</Link> for a list of available error codes.
      </>
    ),
  },
  {
    name: 'R2',
    desc: (
      <>
        Description of the Error Code — a short human-readable message paired with R1. For the full list of codes see <Link to="/technical-documentation/appendix/error-codes" className="text-[#1F4E79] underline hover:text-[#163a5f] font-semibold">Error Codes</Link>.
      </>
    ),
  },
  { name: 'R3',  desc: "Merchant's own reference code to the transaction." },
  { name: 'R4',  desc: 'Transaction status.' },
  {
    name: 'R5',
    desc: (
      <>
        Response from AVS and CV2 checks.
        <ul className="list-disc pl-5 mt-2 space-y-0.5 text-xs">
          <li>All Match MATCH</li>
          <li>Security Code Match Only</li>
          <li>Address Match Only</li>
          <li>No Data Matches</li>
          <li>Data Not Checked</li>
        </ul>
      </>
    ),
  },
  {
    name: 'R6',
    desc: (
      <>
        Result of the checks on the cardholder's address numeric from the AVS/CV2 checks.
        <ul className="list-disc pl-5 mt-2 space-y-0.5 text-xs">
          <li>Match</li>
          <li>Not Match</li>
        </ul>
      </>
    ),
  },
  {
    name: 'R7',
    desc: (
      <>
        Result of the checks on the cardholder's Postcode from the AVS/CV2 checks.
        <ul className="list-disc pl-5 mt-2 space-y-0.5 text-xs">
          <li>Match</li>
          <li>Not Match</li>
        </ul>
      </>
    ),
  },
  {
    name: 'R8',
    desc: (
      <>
        Result of the checks on the cardholder's CV2 code from the AVS/CV2 checks.
        <ul className="list-disc pl-5 mt-2 space-y-0.5 text-xs">
          <li>Match</li>
          <li>Not Match</li>
        </ul>
      </>
    ),
  },
  {
    name: 'R9',
    desc: (
      <>
        Results of the 3D Secure Checks.
        <ul className="list-disc pl-5 mt-2 space-y-0.5 text-xs">
          <li>OK</li>
          <li>Error</li>
        </ul>
      </>
    ),
  },
  {
    name: 'R10',
    desc: (
      <>
        Confirmation Type.
        <ul className="list-disc pl-5 mt-2 space-y-0.5 text-xs">
          <li>Confirm</li>
          <li>Authorisation</li>
        </ul>
      </>
    ),
  },
  { name: 'R11', desc: 'Merchant specified data that will be returned on the Response.' },
  { name: 'R12', desc: 'Merchant specified transaction identifier that will be returned on the response.' },
  { name: 'R13', desc: 'Transaction Response.' },
  { name: 'R14', desc: 'ARN (Acquirer Reference Number).' },
  { name: 'R15', desc: 'eCOMM Transaction ID.' },
  {
    name: 'udf',
    desc: (
      <>
        <Link to="/technical-documentation/api-reference/overview#udf" className="text-[#1F4E79] underline hover:text-[#163a5f]">UDF List</Link> — user-defined fields echoed back from the original request.
      </>
    ),
  },
];

/* ----- PHP / C# handler tabs ----- */
const HandlerTabs = () => {
  const [lang, setLang] = useState('php');
  return (
    <div data-testid="handler-lang-tabs">
      <div className="inline-flex rounded-md bg-gray-100 p-1 mb-3" role="tablist">
        {['php', 'csharp'].map((l) => (
          <button
            key={l}
            role="tab"
            aria-selected={lang === l}
            onClick={() => setLang(l)}
            className={`px-4 py-1.5 text-xs font-semibold rounded transition-all ${
              lang === l ? 'bg-white text-[#1F4E79] shadow-sm' : 'text-gray-600 hover:text-[#1F4E79]'
            }`}
            data-testid={`handler-lang-tab-${l}`}
          >
            {l === 'php' ? 'PHP' : 'C#'}
          </button>
        ))}
      </div>
      {lang === 'php'
        ? <CodeBlock code={PHP_SAMPLE} lang="php" testid="handler-php-code" />
        : <CodeBlock code={CSHARP_SAMPLE} lang="csharp" testid="handler-csharp-code" />
      }
    </div>
  );
};

const ConfirmationNotification = () => (
  <NotificationPageLayout
    currentId="confirmation"
    title="Confirmation"
    titleBadge="Notification"
    subtitle="A confirmation of the final transaction result, POSTed to your Notification URL — the backbone of back-office automation on eCOMM."
    toc={TOC}
    mainTestId="confirmation-notification-page"
    sidebarTestId="confirmation-notification-sidebar"
  >
    {/* Overview */}
    <section id="overview" className="scroll-mt-28 bg-white/80 backdrop-blur-sm rounded-lg shadow-lg p-8 sm:p-10 mb-6">
      <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">Overview</h2>
      <p className="text-gray-700 text-base leading-relaxed mb-4">
        A confirmation is sent to the merchant's desired Notification URL if the merchant requested to receive these by providing eCOMM with their desired setting of the{' '}
        <Link to="/technical-documentation/api-reference/overview#notification-methods" className="text-[#1F4E79] underline hover:text-[#163a5f]">Notification Method</Link>{' '}
        to be either <code className="bg-gray-100 px-1.5 py-0.5 rounded text-sm font-mono">Both</code> or <code className="bg-gray-100 px-1.5 py-0.5 rounded text-sm font-mono">Notification</code>. The confirmation contains the transaction result and any UDF data provided in the original HTTP POST Request sent to the API. A confirmation notification is useful if the merchant wishes to act on the consumer's transaction after the transaction has been completed and if the merchant wants an alternative method to determine transaction completion in case of a server fault — the confirmation notification can then be used as a backup. Below explains the data the merchant can expect to be received in the Notification URL; the merchant can use this data to handle the transaction.
      </p>
      <p className="text-gray-700 text-base leading-relaxed mb-4">
        The merchant must submit their chosen Notification URL to eCOMM for it to be whitelisted and enabled for use. The notification will be sent via <strong>HTTP Form Post in XML format</strong> where the merchant should set up a handler to parse the response.
      </p>
      <p className="text-gray-700 text-base leading-relaxed">
        The XML document will be transmitted through a field called <code className="bg-gray-100 px-1.5 py-0.5 rounded text-sm font-mono">data</code>.
      </p>

      <div className="bg-blue-50/80 border border-blue-200 rounded-lg p-4 flex items-start gap-3 mt-5">
        <Info size={18} className="text-[#1F4E79] flex-shrink-0 mt-0.5" />
        <p className="text-sm text-gray-700">
          Confirmation Notifications are the backbone of back-office automation on eCOMM. Most merchants drive order fulfilment, inventory reservations, receipt emails and CRM updates from this single webhook.
        </p>
      </div>
    </section>

    {/* Setting Up A Handler */}
    <section id="setting-up-handler" className="scroll-mt-28 bg-white/80 backdrop-blur-sm rounded-lg shadow-lg p-8 sm:p-10 mb-6">
      <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">Setting Up A Handler</h2>
      <p className="text-gray-700 text-base leading-relaxed mb-4">
        It is up to the merchant to decide what may be the best decision when deciding on how to handle the confirmation notification. The below example is intended to give the merchant an idea on how this can be achieved.
      </p>
      <p className="text-gray-700 text-base leading-relaxed mb-3">
        If the merchant's notification URL is: <strong><code className="bg-gray-100 px-1.5 py-0.5 rounded text-sm font-mono">http://example.com/Notification-Handler/Confirmation/</code></strong>, the HTTP POST will be sent identical to the below sample URL:
      </p>

      <div id="sample-notification-url" className="scroll-mt-28">
        <CodeBlock code={NOTIFICATION_URL_SAMPLE} lang="http" testid="notification-url-code" />
      </div>

      <p className="text-gray-700 text-sm leading-relaxed mt-4">
        The XML document will be enclosed in the <code className="bg-gray-100 px-1.5 py-0.5 rounded text-xs font-mono">data</code> parameter which must be parsed in order to pull out the information the merchant may need to proceed.
      </p>

      <div id="sample-handler" className="scroll-mt-28 mt-8">
        <h3 className="text-xl font-bold text-gray-900 mb-3">Sample Notification Handler</h3>
        <LangTabs
          samples={{ php: PHP_SAMPLE, csharp: CSHARP_SAMPLE }}
          testidBase="handler-lang"
        />
      </div>
    </section>

    {/* Data Returned */}
    <section id="data-returned" className="scroll-mt-28 bg-white/80 backdrop-blur-sm rounded-lg shadow-lg p-8 sm:p-10 mb-6">
      <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">Data Returned</h2>
      <p className="text-gray-700 text-base leading-relaxed mb-3">
        The confirmation notification will POST an XML document; below will help identify what XML fields are included and what to expect.
      </p>
      <p className="text-gray-700 text-base leading-relaxed mb-4">
        Successful transactions are marked with <strong className="text-[#c32222]">0000</strong> inside the <code className="bg-gray-100 px-1.5 py-0.5 rounded text-sm font-mono">&lt;R1&gt;</code> field.
      </p>
      <p className="text-gray-700 text-base leading-relaxed mb-4 font-semibold">
        A well-defined XML document response will be returned with the following information:
      </p>

      <div className="overflow-x-auto">
        <table className="w-full text-sm border-collapse" data-testid="confirmation-fields-table">
          <thead>
            <tr className="bg-[#1F4E79] text-white">
              <th className="text-left p-3 w-24">Field Name</th>
              <th className="text-left p-3">Description</th>
            </tr>
          </thead>
          <tbody>
            {FIELDS.map((f, i) => (
              <tr key={f.name} className={`border-b border-gray-200 ${i % 2 ? 'bg-gray-50/50' : ''}`}>
                <td className="p-3 font-mono font-semibold text-[#1F4E79] align-top">{f.name}</td>
                <td className="p-3 align-top">{f.desc}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>

    {/* Sample XML */}
    <section id="sample-xml" className="scroll-mt-28 bg-white/80 backdrop-blur-sm rounded-lg shadow-lg p-8 sm:p-10 mb-6">
      <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">Sample XML Document</h2>
      <p className="text-gray-700 text-base leading-relaxed mb-4">
        Below marks a sample successful confirmation notification. The error code <strong className="text-[#c32222]">0000</strong> inside the <code className="bg-gray-100 px-1.5 py-0.5 rounded text-sm font-mono">&lt;R1&gt;</code> field marks this transaction as successful.
      </p>
      <CodeBlock code={SAMPLE_XML} lang="xml" testid="confirmation-sample-code" />
    </section>

    {/* Implementation Checklist (value-add) */}
    <section id="checklist" className="scroll-mt-28 bg-white/80 backdrop-blur-sm rounded-lg shadow-lg p-8 sm:p-10 mb-6">
      <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">Implementation Checklist</h2>
      <p className="text-gray-700 text-base leading-relaxed mb-4">
        A short, opinionated checklist to save you a couple of production incidents.
      </p>
      <ul className="space-y-2 text-gray-700 text-sm leading-relaxed">
        {[
          'Serve your Notification URL over HTTPS and submit it to eCOMM for whitelisting.',
          'Whitelist eCOMM\'s outbound IP range on any WAF / firewall in front of your webhook.',
          'Reply with HTTP 200 OK as soon as the notification is safely persisted — do not block on downstream processing.',
          'Process fulfilment, emails and accounting sync asynchronously after acknowledging.',
          'Be idempotent on R3 (Transaction Code) — eCOMM may redeliver after a retry.',
          'Log the raw data payload for every notification — invaluable evidence for chargeback defence later.',
        ].map((t) => (
          <li key={t} className="flex items-start gap-2">
            <CheckCircle2 size={16} className="text-emerald-600 flex-shrink-0 mt-0.5" />
            <span>{t}</span>
          </li>
        ))}
      </ul>
    </section>
  </NotificationPageLayout>
);

export default ConfirmationNotification;
